import React, {useEffect, useMemo, useRef, useState} from 'react';
import AuthorsComponent from '@/components/atoms/authors';
import AuthorsListComponent from '@/components/atoms/authors-list';
import AuthorsNameComponent from '@/components/atoms/authors/authorsName';
import Image from '@/components/atoms/image';
import Loading from '@/components/atoms/loading';
import NotFound from '@/components/organisms/404';
import {ImageSizes} from '@/constants/image-sizes.constant';
import {Pages} from '@/constants/pages.constant';
import {PostTypeIds} from '@/constants/post-type-ids.constant';
import {useDataLayer, useScript, useTranslation} from '@/hooks';
import {useHttpGet, useHttpPut} from '@/hooks/use-http';
import {useIsInViewport} from '@/hooks/useIsInViewport';
import LocalStorage from '@/observables/localStorage.observable';
import {Row, Col} from 'react-bootstrap';
import Moment from 'react-moment';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import styles from './article.module.scss';
import {POST_BY_URI, ARTICLES_INTERACTIONS, SECTION_BY_UUID} from '@/constants/endpoints.constant';
import ArticleInteraction from '../../molecules/article-interaction';
import CommentSection from '../../molecules/comments-section';
import QuizButton from '@/components/molecules/quiz-button';
import SignInModal from '@/components/molecules/sign-in-modal';
import CardShelf from '@/components/molecules/card-shelf';
import {default as YoutubePlayer} from 'react-player/youtube';
import {default as WistiaPlayer} from 'react-player/wistia';
import {getParsedImageURL} from '@/utils/images';
import {getVideoType, VideoTypeYoutube, VideoTypeWistia} from '@/utils/video';

const sortedAuthors = (authors) => {
  if (!authors?.length) {
    return [];
  }
  return authors.sort(function (a, b) {
    const userA = a.title;
    const userB = b.title;
    return (userA < userB) ? -1 : (userA > userB) ? 1 : 0;
  });
}

const BannerTypeVideo = 'video';

const ArticleComponent = ({hideCollection = false}) => {
  useDataLayer(Pages.POSTS);
  const LIKED = 'liked';
  const location = useLocation() || {};
  const [showMobileAuthorList, setShowMobileAuthorList] = useState(false);
  const [collection, setCollection] = useState([]);
  const {pathname, hash} = location;
  const path = pathname[pathname.length - 1] === '/' ? pathname.slice(0, pathname.length - 1) : pathname;
  const {data: post, execute: getPost, loading: loadingPost, error: restPostError} = useHttpGet();
  const [showComments, setShowComments] = useState(false);
  const [articleInteractions, setArticlesInteractions] = useState({});
  const [countLikes, setCountLikes] = useState(0);
  const {data: interactions, execute: getInteractions, loading: isLoadingInteractions} = useHttpGet();
  const {data: relatedSeries, execute: getRelatedSeries, loading: isLoadingRelatedSeries} = useHttpGet();
  const {execute: editInteractions} = useHttpPut();
  const {t} = useTranslation();
  const articleRef = useRef(null);
  const isInViewport1 = useIsInViewport(articleRef);
  const [showModal, setShowModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const authorsList = sortedAuthors(post?.authors);

  const toggleModal = () => {
    setShowModal(!showModal)
  }

  useEffect(() => {
    getPost(POST_BY_URI(path)).catch(() => {
      //useHttp returns rejected promise
    });
  }, [path]);

  useEffect(() => {
    if (hash && post) {
      getRelatedSeries(SECTION_BY_UUID(hash.substring(1) + '?exclude=' + post?.uuid));
    }
  }, [hash, post])

  const getBanner = (post) => {
    return post?.banner_image?.url || post?.listing_image?.url || undefined;
  };

  const image = getBanner(post);

  const moduleHtml = post?.body;
  const bannerAlt = post?.banner_image?.alt || '';

  // eslint-disable-next-line no-useless-escape
  const regex = /src="https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)"/g;
  const embeddedScripts = useMemo(() => moduleHtml?.match(regex)?.map(url => url.replace('src=', '').replace(/"/g, '')).filter((v, i, a) => a.indexOf(v) === i), [moduleHtml]);

  useScript(embeddedScripts, [moduleHtml], true);

  useEffect(() => {
    if (post?.collection?.status === 'active' && !hideCollection) {
      setCollection(post.collection);
    } else if (post?.collections?.length && post.collections[0].status === 'active' && !hideCollection) {
      setCollection(post.collections[0]);
    }
    if (post?.uuid) {
      getInteractions(ARTICLES_INTERACTIONS(post?.uuid))
    }
  }, [post]);

  useEffect(() => {

  }, [hash]);

  useEffect(() => {
    if (interactions) {
      setArticlesInteractions(interactions)
      setCountLikes(interactions.like_count || 0);
      setIsLoggedIn(!!interactions.logged_in);
    }
  }, [interactions])

  const onInteract = (displayToast, key) => {
    const tempInteractions = {...articleInteractions, [key]: !articleInteractions?.[key]}
    editInteractions(ARTICLES_INTERACTIONS(post?.uuid), tempInteractions);
    setArticlesInteractions(tempInteractions);
    if (key === LIKED) {
      setCountLikes(tempInteractions[LIKED] ? countLikes + 1 : countLikes - 1);
    }
    displayToast(tempInteractions[key] ? key.charAt(0).toUpperCase() + key.slice(1) : `Un${key}`);
  }

  const getAuthorDescription = (post) => {
    return (
      <>
        <h1 className={styles.cgArticle__title}>{post.title}</h1>
        <Row>
          <Col className={styles.cgArticle__author}>
            {authorsList.length && <>
              <div className={styles.cgArticle__authors}>
                {authorsList.map((coAuthor, index) => {
                  return index < 3 &&
                    <AuthorsComponent
                      author={coAuthor}
                      key={index}
                      index={index}
                      showListMobile={setShowMobileAuthorList}
                      raiseName={authorsList.length > 1}/>;
                })}
              </div>
              <div className={styles.cgArticle__authorsName}>
                <div className={styles.cgArticle__authorsContainer}>
                  <AuthorsNameComponent author={authorsList[0]}/>
                  {authorsList.length === 2 &&
                    <div className='d-block d-sm-flex'>&
                      <AuthorsNameComponent className='ml-sm-1' author={authorsList[1]}/>
                    </div>}
                  {authorsList.length > 2 && `& ${authorsList.length - 1} others`}
                </div>
              </div>
            </>}
          </Col>
          <Col className={styles.cgArticle__date}><Moment format="MMMM DD, YYYY"
                                                          locale={LocalStorage.getItem('i18nextLng') || 'en'}>{post.published_at}</Moment></Col>
        </Row>
      </>
    )
  }

  const displayLoading = () => {
    if (loadingPost) {
      return <Loading/>;
    }
  }

  const displayContent = () => {
    if (!loadingPost && post) {
      return (
        <>
          {post && getAuthorDescription(post)}
          {moduleHtml && <div className={`${styles.cgArticle__outerContainer__dividerTop}`}/>}
          {post?.quiz_summary &&
            <QuizButton points={post?.quiz_summary?.reward_stars} quizUuid={post?.quiz_summary?.quiz_uuid}
                        articleTitle={post?.title} collection={collection?.url?.url}
                        completed={post?.quiz_summary?.completed}/>
          }
          <div className={`${styles.cgArticle__content}`}>
            {post?.type === PostTypeIds.VIDEO && (
              <div
                itemScope
                itemType="https://schema.org/VideoObject"
                dangerouslySetInnerHTML={{__html: moduleHtml}}
              ></div>
            )}
            {post?.type === PostTypeIds.PODCAST && (
              <div
                itemScope
                itemType="https://schema.org/PodcastEpisode"
                dangerouslySetInnerHTML={{__html: moduleHtml}}
              ></div>
            )}
            {post?.type !== PostTypeIds.PODCAST && post?.type !== PostTypeIds.VIDEO && (
              <div dangerouslySetInnerHTML={{__html: moduleHtml}}></div>
            )}
          </div>
        </>
      );
    }
  }

  const displayBannerAndArticleImage = () => {
    if (!loadingPost && !post) {
      return <NotFound></NotFound>
    }
    if (!post) {
      return null;
    }

    // if has collection and is a content type of article
    if (!!collection?.name && post?.type === PostTypeIds.ARTICLE) {
      return (
        <div className={styles.cgArticle__banner} style={{
          backgroundColor: collection?.color
        }}>
          <div className={styles.cgArticle__banner__imageContainer}>
            <Image
              className={styles.cgArticle__image}
              alt={bannerAlt}
              src={image}
              size={ImageSizes.BANNER}
              eager
            />
            <div className={styles.cgArticle__label}
                 style={{backgroundColor: `#${collection?.color}`}}>{collection?.name} Collection
            </div>
          </div>
          <div
            style={{
              backgroundImage: `url(${getParsedImageURL(collection?.background_image?.location, ImageSizes.FULL)})`,
              backgroundPositionY: `${collection?.offset_y_article}%`
            }}
            className={styles.cgArticle__bannerBackground}>
          </div>
        </div>
      );
    }
    // otherwise either is a collection and not an article (in which case we'll ignore that it's a collection)
    // any content type, but not in a collection, either way we can ignore the collection use case

    // if type is a video,
    if (post?.type === PostTypeIds.VIDEO && post?.data?.banner_type === BannerTypeVideo && post?.data?.banner_video) {
      const videoType = getVideoType(post?.data?.banner_video);
      return (
        <div
          className={styles.cgArticle__banner__videoContainer}>
          {videoType === VideoTypeYoutube &&
            <YoutubePlayer
              className={styles.cgArticle__banner__videoContainer__player}
              url={post?.data?.banner_video}
              width={'100%'}
              height={'100%'}
              controls={true}
              playerVars={{
                color: '4e61ed',
              }}
            />
          }
          {videoType === VideoTypeWistia &&
            <WistiaPlayer
              className={styles.cgArticle__banner__videoContainer__player}
              url={post?.data?.banner_video}
              width={'100%'}
              height={'100%'}
              controls={true}
              options={{
                videoFoam: true,
                playerColor: '4e61ed',
                controlsVisibleOnLoad: false,
              }}
            />
          }
        </div>
      )
    }

    if (post?.type === PostTypeIds.PODCAST && post?.data?.banner_type === BannerTypeVideo && post?.data?.banner_video) {
      const videoType = getVideoType(post?.data?.banner_video);
      if (videoType === VideoTypeYoutube) {
        return (
          <div className={styles.cgArticle__banner__videoContainer}>
            <YoutubePlayer
              className={styles.cgArticle__banner__videoContainer__player}
              url={post?.data?.banner_video}
              width={'100%'}
              height={'100%'}
              controls={true}
              playerVars={{
                color: '4e61ed',
              }}
            />
          </div>
        )
      }
    }

    if (!image) {
      return null;
    }
    return (
      <div className={styles.cgArticle__banner__imageContainer}>
        <Image
          className={`${styles.cgArticle__image} ${styles.cgArticle__image__no_collection}`}
          alt={bannerAlt}
          src={image}
          size={ImageSizes.BANNER}
          eager
        />
      </div>
    );
  }

  const displayPodcastPlayer = () => {
    if (post?.type === PostTypeIds.PODCAST) {
      if (!post?.data?.podco_id) {
        return null;
      }
      return (
        <div dangerouslySetInnerHTML={{ __html: `<iframe data-target="${post?.data?.podco_id}"
                src="https://play.pod.co/${post?.data?.podco_id}?mini=true" frameBorder="0"
                width="100%" scrolling="no" style="overflow:hidden;max-width:none;height:40px;"
                class="podcastdotco-player podcastdotco-player--mini fr-draggable"></iframe>`}} />
      )
    }
  }

  const displayPost = () => {
    return (
      <>
        <div className={styles.cgArticle}>
          {displayBannerAndArticleImage()}
          {displayPodcastPlayer()}
          <div>
            <div className={styles.cgArticle__outerContainer}>
              {displayLoading()}
              {displayContent()}
              <div ref={articleRef} className={styles.cgArticle__stickyScrollHandle}>
                {!loadingPost && !isLoadingInteractions && !restPostError && !showComments &&
                  <ArticleInteraction
                    commentCount={articleInteractions.comment_count || 0}
                    likesCount={countLikes}
                    isSaved={articleInteractions.saved} isLiked={articleInteractions.liked}
                    onCommentsClicked={() => {
                      setShowComments(!showComments)
                    }}
                    onLikesClicked={onInteract}
                    onSaveClicked={onInteract}
                    article={post}
                    isCommentOpen={showComments}
                    isPartOfCollection={post?.collections?.length > 0}
                    isSticky={!isInViewport1}
                    showLogin={toggleModal}
                    isLoggedIn={isLoggedIn}
                  />
                }
              </div>
              {!loadingPost && !isLoadingInteractions && !restPostError && showComments &&
                <CommentSection
                  isOpen={showComments} onClose={() => {
                  setShowComments(false)
                }}
                  articleTitle={post?.title}
                  articleId={post?.uuid}
                  isLoggedIn={isLoggedIn}
                  showLogin={toggleModal}
                  commentCount={articleInteractions.comment_count || 0}
                />
              }
              {collection?.articles?.length > 1 && <CardShelf
                title={t('articlePageTranslations.moreFromCollection')}
                list={collection?.articles}
                showMoreLink={collection?.url?.url}
                className={styles.cgArticle__outerContainer__collection}
                cardClassName={styles.cgArticle__outerContainer__collection__card}
              />}
              {(!isLoadingRelatedSeries && relatedSeries?.content?.length > 0) && <CardShelf
                title={t('articlePageTranslations.moreFromSeries')}
                list={relatedSeries?.content}
                showMoreLink={relatedSeries?.url?.url}
                className={styles.cgArticle__outerContainer__collection}
                cardClassName={styles.cgArticle__outerContainer__collection__card}
              />}
              <SignInModal show={showModal} onClose={toggleModal}/>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {showMobileAuthorList ?
        <div className='d-block d-md-none d-lg-none'>
          <AuthorsListComponent className='d-block d-md-none d-lg-none' authors={authorsList}
                                onBackArticle={setShowMobileAuthorList}></AuthorsListComponent>
        </div> :
        displayPost()
      }
    </>
  );
}

ArticleComponent.propTypes = {
  hideCollection: PropTypes.bool,
}

export default ArticleComponent;
